import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Paper,
  Typography,
  Divider,
  Button,
  Select,
  MenuItem
} from '@mui/material';
import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import axios from 'axios';
import { useState, useEffect } from 'react';
import { motion } from "framer-motion";
 
export default function Pricing() {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.down('md'));
 
  const [hovered, setHovered] = useState({ basic: false, standard: false, premium: false });
  const [geoInfo, setGeoInfo] = useState({ countryName: '', currency: '' });
  const [exchangeRates, setExchangeRates] = useState({});
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [pricingInSelectedCurrency, setPricingInSelectedCurrency] = useState({ basic: 0, standard: 0, premium: 0 });
 
  const basePricesINR = { basic: 999, standard: 1753, premium: 2999 };
 
    const fadeInAnimationVariants = {
    initial: { opacity: 0, y: 50 },
    animate: (index) => ({
      opacity: 1,
      y: 0,
      transition: { delay: 0.05 * index },
    }),
  };
 
  useEffect(() => {
    const fetchGeoAndRates = async () => {
      try {
        const geoRes = await axios.get('https://ipapi.co/json/');
        const geoData = geoRes.data;
        const userCurrency = geoData.currency;
 
        setGeoInfo({ countryName: geoData.country_name, currency: userCurrency });
 
        const rateRes = await axios.get('https://open.er-api.com/v6/latest/INR');
        const rates = rateRes.data.rates;
        setExchangeRates(rates);
 
        setSelectedCurrency(userCurrency);
 
        if (rates[userCurrency]) {
          setPricingInSelectedCurrency({
            basic: (basePricesINR.basic * rates[userCurrency]).toFixed(2),
            standard: (basePricesINR.standard * rates[userCurrency]).toFixed(2),
            premium: (basePricesINR.premium * rates[userCurrency]).toFixed(2),
          });
        }
      } catch (error) {
        console.error('Error fetching geo or exchange data:', error);
      }
    };
 
    fetchGeoAndRates();
  }, []);
 
  const handleCurrencyChange = (currency) => {
    setSelectedCurrency(currency);
    const rate = exchangeRates[currency];
    if (rate) {
      setPricingInSelectedCurrency({
        basic: (basePricesINR.basic * rate).toFixed(2),
        standard: (basePricesINR.standard * rate).toFixed(2),
        premium: (basePricesINR.premium * rate).toFixed(2),
      });
    }
  };
 
  const plans = [
    {
      name: 'Basic',
      price: pricingInSelectedCurrency.basic,
      features: ['SkillSphere Assessment','Detailed report'],
    },
    {
      name: 'Standard',
      price: pricingInSelectedCurrency.standard,
      features: ['SkillSphere Assessment','Detailed report', 'Personal counselling session', 'Career Guidance'],
    },
    {
      name: 'Premium',
      price: pricingInSelectedCurrency.premium,
      features: ['SkillSphere Assessment','Detailed report', 'Personal counselling session', 'Career Guidance', 'Career club membership', 'Beginners module delivery'],
    },
  ];
 
  return (
    <motion.div variants={fadeInAnimationVariants}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }} style={{
      width: '100%',
      backgroundColor: 'white',
      marginTop:'30px',
      display: 'flex',
      justifyContent: 'center',
      padding: isSm ? '20px 0' : '40px 0',
    }}>
      <div style={{
        width: '90%',
        maxWidth: 1200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <Typography sx={{
          fontSize: '40px',
          fontFamily: 'Montserrat',
          fontWeight: '700',
          textAlign: 'center'
        }}>
          <span style={{ color: '#377dff' }}>Our </span>
          <span style={{ color: '#000' }}>Packages</span>
        </Typography>
 
        <Select
          value={selectedCurrency}
          onChange={(e) => handleCurrencyChange(e.target.value)}
          sx={{ mt: 3, mb: 4, width: 150, backgroundColor: 'white' }}
        >
          {Object.keys(exchangeRates).map((currency) => (
            <MenuItem key={currency} value={currency}>
              {currency}
            </MenuItem>
          ))}
        </Select>
 
        <div style={{
          display: 'flex',
          flexDirection: isMd ? 'column' : 'row',
          alignItems: isMd ? 'center' : 'stretch',
          justifyContent: 'center',
          gap: 24,
          width: '100%',
          flexWrap: 'wrap'
        }}>
          {plans.map((plan) => (
            <Paper
              key={plan.name}
              elevation={3}
              onMouseEnter={() => setHovered(prev => ({ ...prev, [plan.name.toLowerCase()]: true }))}
              onMouseLeave={() => setHovered(prev => ({ ...prev, [plan.name.toLowerCase()]: false }))}
              sx={{
                width: isSm ? '90vw' : isMd ? 300 : 350,
                minHeight: 500,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                borderRadius: '16px',
                overflow: 'hidden',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                flexGrow: 1,
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 6,
                },
              }}
            >
              <div style={{
                backgroundImage: 'url("https://dbqa318yoj3nn.cloudfront.net/pricing_BG.png")',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                color: 'white',
                padding: '20px',
              }}>
                <Typography sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#FFD700'
                }}>
                  {plan.name}
                </Typography>
                <Divider sx={{ borderBottom: '2px solid white', my: 1 }} />
                <Typography sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '22px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  {selectedCurrency}
                  <span>{plan.price}</span>
                </Typography>
              </div>
              <div style={{
                backgroundColor: '#f5f5f5',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                flexGrow: 1,
              }}>
                <div>
                  {plan.features.map((feature, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                      <CheckRoundedIcon sx={{ fontSize: '20px', color: '#7159ef', marginRight: '10px' }} />
                      <Typography sx={{
                        fontFamily: 'Montserrat',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#333'
                      }}>
                        {feature}
                      </Typography>
                    </div>
                  ))}
                </div>
                <Button
                  variant="contained"
                  onClick={() => {
                    const name = plan.name.toLowerCase();
                    const inrPrice = basePricesINR[name]; // Use INR price here
                    const encodedPrice = btoa(inrPrice.toString());
                    const url = `https://skillsphere.simplifyingskills.com/assessment/payment/uuid/?price=${encodedPrice}&name=${name}
 `;
                    window.location.href = url;
                  }}
                  sx={{
                    mt: 2,
                    backgroundColor: '#0d0f28',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: '600',
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: '#1c1f4a'
                    },
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 2,
                  }}
                  fullWidth
                  endIcon={<ArrowForwardIcon />}
                >
                  Purchase Now
                </Button>
              </div>
            </Paper>
          ))}
        </div>
      </div>
    </motion.div>
  );
}